import { Add } from '@mui/icons-material'
import { Box, Button, IconButton, Tooltip, Typography, Alert, AlertTitle } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded'
import { useQuery } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import BlockIcon from '@mui/icons-material/Block'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

import DefaultMenu, { IMenuItemProps } from 'app/components/menu/DefaultMenu'
import Search from 'app/components/search/Search'
import { PageContentContainer } from 'app/components/styles'
import DataTable from 'app/components/table/table/table'
import TableHeader from 'app/components/table/tableHeader/TableHeader'
import { ContentBody } from 'app/styles'
import { ITableHeadCell, Order } from 'core/models/table'
import { fecthProduct } from 'core/querryes/product/productQuerry'
import theme from 'core/theme/theme'
import DataTablePagination from 'app/components/table/pagination/pagination'
import RegisterProductModal from 'app/components/modals/productModal/registerProductModal'
import CardProduct from './cardProduct/cardProduct'
import { SET_SWITCH_STATE } from 'core/redux/slices/saleSlice/saleSlice'
import { RootState } from 'core/redux/store'
import ProductDetailsModal from 'app/components/modals/detailsModal/productDetails/ProductDetailsModal'
import EditProductModal from 'app/components/modals/productModal/editProductModal'
import NewDefaultFilter from 'app/components/filter/newDefaultFilter'
import DefaultDialog from 'app/components/defaultDialog/defaultDialog'
import { ProductService } from 'core/api/product/productService'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { StyledDialog, StyledDialogTitle, StyledDialogActions } from 'app/components/defaultDialog/styles'
import { useAppSelector } from 'core/hooks/reduxHooks'

const maskValue = (value: any) => '••••••'

// Headers condicionais baseado em permissões - ATUALIZADO
const getTableHeaders = (hasPermission: boolean): ITableHeadCell[] => {
    const baseHeaders: ITableHeadCell[] = [
        { name: 'name', label: 'Nome', align: 'left' },
        { name: 'amount', label: 'Quantidade', align: 'left' },
        { name: 'sku', label: 'Código do produto', align: 'left' },
    ]

    // Usuários limitados veem preço de revenda e etiqueta, mas NÃO o de custo
    const limitedHeaders: ITableHeadCell[] = [
        { name: 'resalePrice', label: 'P. de Revenda', align: 'center' },
        { name: 'priceTag', label: 'P. de Etiqueta', align: 'center' },
    ]

    // Usuários com permissão completa veem todos os preços
    const fullHeaders: ITableHeadCell[] = [
        { name: 'priceCost', label: 'P. de Custo', align: 'center' },
        { name: 'resalePrice', label: 'P. de Revenda', align: 'center' },
        { name: 'priceTag', label: 'P. de Etiqueta', align: 'center' },
    ]

    const finalHeaders: ITableHeadCell[] = [
        { name: 'statusProduct', label: 'Status', align: 'center' },
    ]

    const actionsHeader: ITableHeadCell = { name: 'actions2', label: 'Opções', align: 'left' }

    if (hasPermission) {
        return [...baseHeaders, ...fullHeaders, ...finalHeaders, actionsHeader]
    }

    // Usuários limitados veem alguns preços e têm acesso às opções (só detalhes)
    return [...baseHeaders, ...limitedHeaders, ...finalHeaders, actionsHeader]
}

const filterItems: any[] = [
    { name: 'Nome', value: 'name', type: 'texto' },
    { name: 'Código do produto', value: 'sku', type: 'texto' },
    {
        name: 'Status do produto',
        value: 'statusProduct',
        options: [
            { key: 'Em Estoque', value: 'AVAILABLE' },
            { key: 'Baixo Estoque', value: 'LOW_STOCK' },
            { key: 'Em Falta', value: 'OUT_OF_STOCK' }
        ]
    }
]

// Componente para exibir acesso negado a valores
const RestrictedValueDisplay = ({ label }: { label: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
        <VisibilityOffIcon sx={{ fontSize: 16, color: '#999' }} />
        <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
            Restrito
        </Typography>
    </Box>
)

function Product() {
    const dispatch = useDispatch()
    const { switchState } = useSelector((state: RootState) => state.sale)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [page, setPage] = useState(0)
    const [isOpenRegisterModal, setIsOpenRegisterModal] = useState(false)
    const [isOpenEditModal, setIsOpenEditModal] = useState(false)
    const [count, setCount] = useState(0)
    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState('name')
    const [filterModal, setFilterModal] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [details, setDetails] = useState(false)
    const [idProduct, setIdProduct] = useState<string | undefined>()
    const [deleteDialog, setDeleteDialog] = useState(false)
    const [exportInventory, setExportInventory] = useState(false)
    const [isLoadingExport, setIsLoadingExport] = useState(false)
    const [haveImagesEdit, setHaveImagesEdit] = useState(false)

    // Controle de permissões
    const basicUserInfo = useAppSelector((state) => state.auth.userInfo)
    const hasAdminRole = basicUserInfo?.roles?.includes("ROLE_ADMIN") ?? false
    const hasManagementPermission = hasAdminRole

    const open = Boolean(anchorEl)

    const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
    }

    const handleAccessRowById = (id: string) => {
        setIdProduct(id)
    }

    // Menu items condicionais baseado em permissões
    const getMenuItems = (): IMenuItemProps[] => {
        // Usuários sem permissão de gerenciamento podem ver apenas detalhes
        if (!hasManagementPermission) {
            return [
                {
                    function: () => {
                        setDetails(true)
                        handleAccessRowById(idProduct || '')
                        handleCloseMenu()
                    },
                    label: 'Detalhes do produto'
                }
            ]
        }

        // Usuários com permissão de gerenciamento veem tudo
        return [
            {
                function: () => {
                    setIsOpenEditModal(true)
                    handleCloseMenu()
                },
                label: 'Editar produto'
            },
            {
                function: () => {
                    setDetails(true)
                    handleAccessRowById(idProduct || '')
                    handleCloseMenu()
                },
                label: 'Detalhes do produto'
            },
            ...(hasAdminRole ? [{
                function: () => {
                    setDeleteDialog(true)
                    handleAccessRowById(idProduct || '')
                    handleCloseMenu()
                },
                label: 'Deletar produto'
            }] : [])
        ]
    }

    const handleDeleteProduct = async () => {
        if (!hasAdminRole) return
        await ProductService.deleteProduct(String(idProduct)).then((resp) => {
            setDeleteDialog(false)
            refetch()
        })
        setDeleteDialog(false)
    }

    const handlExportInventory = async () => {
        if (!hasManagementPermission) return
        setIsLoadingExport(true)
        await ProductService.exportInventory().then((resp) => {
            setIsLoadingExport(false)
        })
        setExportInventory(false)
        setIsLoadingExport(false)
    }

    const [searchParams, setSearchParams] = useSearchParams()
    const name = searchParams.get('name') || ''
    const sku = searchParams.get('sku') || ''
    const statusProduct = searchParams.get('statusProduct') || ''

    const { data, isLoading, isSuccess, refetch } = useQuery({
        queryKey: [
            'product',
            page,
            rowsPerPage,
            orderBy,
            order,
            name,
            sku,
            statusProduct
        ],
        queryFn: () =>
            fecthProduct(
                page,
                rowsPerPage,
                orderBy,
                order,
                name,
                sku,
                statusProduct
            )
    })

    const handleSkuChange = (value: string | undefined) => {
        const newParams = new URLSearchParams(searchParams)
        if (value) {
            newParams.set('sku', value ?? '')
        } else {
            newParams.delete('sku')
        }
        setSearchParams(newParams)
    }

    useEffect(() => {
        if (isSuccess && data) {
            setCount(data.totalElements)
        }
    }, [isSuccess, data])

    const handleClickSwitch = () => {
        dispatch({ type: SET_SWITCH_STATE, payload: !switchState })
    }

    const selectedProduct = data?.content.find(
        (collection: any) => collection.id === idProduct
    )

    // Função para mascarar dados sensíveis nos produtos
    const processProductData = (products: any[] | undefined) => {
        if (!products || !Array.isArray(products)) return []

        if (hasManagementPermission) {
            return products
        }

        // Usuários limitados não veem apenas o preço de custo
        return products.map(product => ({
            ...product,
            priceCost: maskValue(product.priceCost), // Apenas preço de custo mascarado
            // resalePrice e priceTag permanecem visíveis
        }))
    }


    // Headers dinâmicos baseado em permissões
    const tableHeaders = getTableHeaders(hasManagementPermission)
    const menuItems = getMenuItems()

    return (
        <PageContentContainer>
            <TableHeader
                filterBtn={true}
                filterBtnAction={() => setFilterModal(true)}
                switchActionFunction={handleClickSwitch}
                // Condicional: só mostra botão de cadastrar se tiver permissão
                {...(hasManagementPermission && {
                    mainActionFunction: () => setIsOpenRegisterModal(true),
                    mainActionLabel: "Cadastrar Produto",
                    mainIcon: <Add sx={{ color: theme.COLORS.YELLOW2 }} />
                })}
                status={true}
                extraComponents={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}>
                        {/* Botão de exportar só aparece com permissão */}
                        {hasManagementPermission && (
                            <Tooltip title="Exportar Inventário">
                                <IconButton onClick={() => setExportInventory(true)}>
                                    <FileDownloadOutlinedIcon sx={{ color: theme.COLORS.YELLOW2 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Search
                            searchPlaceHolder="Código do produto..."
                            type="text"
                            querrySearching={isLoading}
                            querry={sku}
                            onChange={handleSkuChange}
                        />
                    </Box>
                }
            />
            <ContentBody>
                {switchState ? (
                    <CardProduct
                        refetch={refetch}
                        data={processProductData(data?.content)}
                    />
                ) : (
                    <DataTable
                        head={tableHeaders}
                        data={processProductData(data?.content)}
                        order={order}
                        orderBy={orderBy}
                        setOrder={setOrder}
                        setOrderBy={setOrderBy}
                        isLoading={isLoading}
                        accessRowById={handleAccessRowById}
                        // Menu só aparece se tiver permissão
                        menu={
                            <Tooltip title="Opções">
                                <IconButton onClick={handleClickMenu}>
                                    <MoreHorizRounded />
                                </IconButton>
                            </Tooltip>
                        }
                    />
                )}

                {/* Menus e modais */}
                <DefaultMenu
                    anchor={anchorEl}
                    menuItems={menuItems}
                    onClose={handleCloseMenu}
                    status={open}
                />

                {/* Modal de detalhes - disponível para todos */}
                {selectedProduct && details && (
                    <ProductDetailsModal
                        product={selectedProduct}
                        isOpen={details}
                        onClose={() => setDetails(false)}
                        onOpen={() => setDetails(true)}
                    />
                )}

                {/* Modais administrativos condicionais */}
                {hasManagementPermission && (
                    <>
                        <RegisterProductModal
                            refetch={refetch}
                            isOpen={isOpenRegisterModal}
                            onClose={() => setIsOpenRegisterModal(false)}
                            onOpen={() => setIsOpenRegisterModal(true)}
                        />

                        {selectedProduct && isOpenEditModal && (
                            <EditProductModal
                                setHaveImagesEdit={setHaveImagesEdit}
                                product={selectedProduct}
                                isOpen={isOpenEditModal}
                                onClose={() => setIsOpenEditModal(false)}
                                onOpen={() => setIsOpenEditModal(true)}
                                refetch={refetch}
                            />
                        )}

                        {hasAdminRole && (
                            <DefaultDialog
                                title="Deseja realmente deletar o produto?"
                                confirmAction={() => handleDeleteProduct()}
                                isOpen={deleteDialog}
                                onCloseAction={() => setDeleteDialog(false)}
                            />
                        )}

                        <DefaultDialog
                            title="Deseja realmente exportar o inventário?"
                            confirmAction={() => handlExportInventory()}
                            isOpen={exportInventory}
                            onCloseAction={() => setExportInventory(false)}
                            disabled={isLoadingExport}
                        />

                        <StyledDialog open={exportInventory}>
                            <StyledDialogTitle>{"Deseja realmente exportar o inventário?"}</StyledDialogTitle>
                            <StyledDialogActions>
                                <Button variant="contained" onClick={() => setExportInventory(false)} disabled={isLoadingExport}>
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={isLoadingExport}
                                    onClick={() => handlExportInventory()}
                                    autoFocus
                                >
                                    {isLoadingExport ? "Exportando..." : "Exportar"}
                                </Button>
                            </StyledDialogActions>
                        </StyledDialog>
                    </>
                )}

                <DataTablePagination
                    setPage={setPage}
                    page={page}
                    setRowsPerPage={setRowsPerPage}
                    rowsPerPage={rowsPerPage}
                    count={count}
                />
                <NewDefaultFilter
                    isOpen={filterModal}
                    items={filterItems}
                    onClose={() => setFilterModal(false)}
                    onOpen={() => setFilterModal(true)}
                    title="Filtrar Produtos"
                    changePage={setPage}
                />
            </ContentBody>
        </PageContentContainer>
    )
}

export default Product